import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { PARTNERS_SITE_URL } from '../lib/links';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_LENGTH = 8;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check the link before the partner types anything — an expired link should say so
  // up front, not after they have chosen a password.
  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API_URL}/api/auth/reset-password/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setValid(data.valid);
        setEmail(data.email || '');
      })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('The two passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      toast.success('Password updated. Sign in with it now.');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset the password');
    } finally {
      setLoading(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col justify-center px-8 sm:px-12 py-12">
      <div className="max-w-md w-full mx-auto">
        <Link to="/login" className="flex w-fit items-center gap-2 text-[#64748B] hover:text-[#1A1A1A] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to sign in</span>
        </Link>
        <a href={PARTNERS_SITE_URL} className="block w-fit mb-8">
          <img src="/light_logo.webp" alt="Expedify Logo" className="h-10 w-auto" />
        </a>
        {children}
      </div>
    </div>
  );

  if (checking) {
    return shell(
      <div className="flex items-center gap-3 text-[#64748B]">
        <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#ee785b]" />
        Checking your link...
      </div>
    );
  }

  if (!valid) {
    return shell(
      <div data-testid="reset-invalid">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-5">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>
        <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2">This link has expired</h1>
        <p className="text-[#64748B] mb-8">
          Reset links work once and last an hour. Ask for a fresh one and it will arrive in a
          minute.
        </p>
        <Link to="/forgot-password">
          <Button className="h-12 px-6 bg-[#ee785b] hover:bg-[#d66547] text-white rounded-lg font-semibold btn-press">
            Send a new link
          </Button>
        </Link>
      </div>
    );
  }

  return shell(
    <>
      <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2">Choose a new password</h1>
      <p className="text-[#64748B] mb-8">
        {email ? <>Setting the password for <span className="font-medium text-[#1A1A1A]">{email}</span>.</> : null}{' '}
        At least {MIN_LENGTH} characters.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label htmlFor="password" className="text-sm font-medium text-[#1A1A1A]">
            New password
          </Label>
          <div className="relative mt-1.5">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-12 rounded-lg border-[#E2E2DF] focus:ring-2 focus:ring-[#ee785b]/20 focus:border-[#ee785b] pr-10"
              minLength={MIN_LENGTH}
              required
              data-testid="reset-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A1A1A]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="confirm" className="text-sm font-medium text-[#1A1A1A]">
            Confirm password
          </Label>
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            className="mt-1.5 h-12 rounded-lg border-[#E2E2DF] focus:ring-2 focus:ring-[#ee785b]/20 focus:border-[#ee785b]"
            minLength={MIN_LENGTH}
            required
            data-testid="reset-confirm"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#ee785b] hover:bg-[#d66547] text-white rounded-lg font-semibold btn-press"
          data-testid="reset-submit"
        >
          {loading ? 'Saving...' : 'Set new password'}
        </Button>
      </form>
    </>
  );
};

export default ResetPasswordPage;
